// Drag-to-create text area for the Designer canvas, extracted from Designer.tsx.
// Owns the textDragMode flag. Toggle it on from the Text tool — the next
// mouse:down/move/up cycle on the canvas draws out a rectangle, then turns it
// into a Textbox with those exact dimensions. Alignment then works inside that
// fixed-width box. Verbatim logic move; the canvas ref + garment colour (for the
// default text fill) come in via deps.
import { useState, useEffect } from 'react'
import type { RefObject } from 'react'
import { fabric } from 'fabric'

export interface UseTextDragCreateDeps {
  fabricRef: RefObject<fabric.Canvas | null>
  colourHex: string | undefined
}

export function useTextDragCreate(deps: UseTextDragCreateDeps) {
  const { fabricRef, colourHex } = deps
  const [textDragMode, setTextDragMode] = useState(false)

  useEffect(() => {
    const c = fabricRef.current; if (!c || !textDragMode) return
    let start: { x: number; y: number } | null = null
    let preview: fabric.Rect | null = null
    const fill = colourHex && colourHex.toLowerCase() === '#0f1117' ? '#ffffff' : '#0F1117'

    function onDown(opt: fabric.IEvent) {
      const p = c!.getPointer(opt.e as MouseEvent)
      start = { x: p.x, y: p.y }
      preview = new fabric.Rect({
        left: p.x, top: p.y, width: 0, height: 0,
        fill: 'rgba(200,169,110,0.08)', stroke: '#C8A96E', strokeDashArray: [4, 4], strokeWidth: 1,
        selectable: false, evented: false,
      })
      c!.add(preview)
    }
    function onMove(opt: fabric.IEvent) {
      if (!start || !preview) return
      const p = c!.getPointer(opt.e as MouseEvent)
      preview.set({
        width:  Math.abs(p.x - start.x),
        height: Math.abs(p.y - start.y),
        left:   Math.min(p.x, start.x),
        top:    Math.min(p.y, start.y),
      })
      c!.requestRenderAll()
    }
    function onUp(opt: fabric.IEvent) {
      if (!start) { setTextDragMode(false); return }
      const p = c!.getPointer(opt.e as MouseEvent)
      const w = Math.abs(p.x - start.x); const h = Math.abs(p.y - start.y)
      if (preview) c!.remove(preview)
      if (w > 20 && h > 12) {
        const tb = new fabric.Textbox('Your text', {
          left: Math.min(p.x, start.x), top: Math.min(p.y, start.y),
          width: w,
          fontFamily: 'Barlow Condensed', fontSize: Math.max(20, Math.min(64, Math.round(h * 0.6))),
          fontWeight: 'bold', fill, textAlign: 'left',
        })
        c!.add(tb); c!.setActiveObject(tb)
        tb.enterEditing(); tb.selectAll()
      }
      start = null; preview = null
      setTextDragMode(false)
      c!.selection = true
      c!.requestRenderAll()
    }

    c.selection = false
    c.defaultCursor = 'crosshair'
    c.on('mouse:down', onDown)
    c.on('mouse:move', onMove)
    c.on('mouse:up', onUp)
    return () => {
      c.off('mouse:down', onDown)
      c.off('mouse:move', onMove)
      c.off('mouse:up', onUp)
      c.selection = true
      c.defaultCursor = 'default'
      if (preview) c.remove(preview)
    }
  }, [textDragMode, colourHex])

  return { textDragMode, setTextDragMode }
}
